<template>
  <PhysicalCardOpenHistoryDetailPage
    v-if="isPhysicalCard"
    :record="record"
    :status="status"
  />

  <main v-else class="card-open-history-detail-page">
    <AppNavBar
      :title="t('card.openCardHistoryDetailTitle', { name: cardName })"
      background-color="#ffffff"
      fallback-to="/wallet/open-card/history"
    />

    <template v-if="status === 'approved'">
      <section class="card-open-detail-hero card-open-detail-hero--approved">
        <app-image
          class="card-open-detail-success-image"
          :src="successImage"
          alt=""
        />
        <p class="card-open-detail-amount">
          <strong>-{{ feeAmount }}</strong>
          <span>{{ feeSymbol }}</span>
        </p>
        <span class="card-open-detail-status-card is-approved">
          {{ t("card.openCardHistorySuccessTitle") }}
        </span>
      </section>
    </template>

    <template v-else-if="status === 'pending'">
      <section class="card-open-detail-hero card-open-detail-hero--pending">
        <app-image
          class="card-open-detail-pending-image"
          :src="physicalPendingImage"
          alt=""
        />
        <h1>{{ t("card.openCardHistoryPendingTitle") }}</h1>
        <span class="card-open-detail-status-card is-pending">
          {{ t("card.openCardHistoryPendingStatus") }}
        </span>
        <p class="card-open-detail-pending-desc">
          {{ t("card.openCardHistoryPendingDesc") }}
        </p>
      </section>

      <section class="card-open-detail-pending-notice">
        <app-image
          :src="pendingImage"
          width="42"
          height="42"
          fit="contain"
          alt=""
        />
        <div>
          <strong>{{ t("card.openCardHistoryPendingNoticeTitle") }}</strong>
          <span>{{ t("card.openCardHistoryPendingNoticeDesc") }}</span>
        </div>
      </section>

      <button
        type="button"
        class="card-open-detail-support"
        @click="router.push('/mine/support-chat')"
      >
        <app-image
          :src="supportIcon"
          width="20"
          height="20"
          fit="contain"
          alt=""
        />
        <span>{{ t("card.openCardHistoryContactSupport") }}</span>
      </button>
    </template>

    <template v-else>
      <section class="card-open-detail-hero card-open-detail-hero--rejected">
        <div class="card-open-detail-rejected-image">
          <app-image :src="rejectedImage" alt="" />
        </div>
        <h1>{{ t("card.openCardHistoryRejectedTitle") }}</h1>
        <span class="card-open-detail-status-card is-rejected">
          {{ t("card.openCardHistoryRejectedStatus") }}
        </span>
        <p class="card-open-detail-failure-reason">
          {{ t("card.openCardHistoryFailureReason") }}{{ failureReason }}
        </p>
        <p class="card-open-detail-failure-desc">
          {{ t("card.openCardHistoryFailureDesc", { name: cardName }) }}
        </p>
      </section>
    </template>

    <section v-if="status !== 'pending'" class="card-open-detail-info">
      <h2>{{ t("card.openCardHistoryInfo") }}</h2>
      <div class="card-open-detail-info__list">
        <div class="card-open-detail-info__row">
          <div class="card-open-detail-info__label">
            {{ t("card.openCardHistoryCardName") }}
          </div>
          <div class="card-open-detail-info__value">{{ cardName }}</div>
        </div>
        <div class="card-open-detail-info__row">
          <div class="card-open-detail-info__label">
            {{ t("card.openCardHistoryAppliedAt") }}
          </div>
          <div class="card-open-detail-info__value">{{ appliedAt }}</div>
        </div>
        <div class="card-open-detail-info__row">
          <div class="card-open-detail-info__label">
            {{ t("card.openCardHistoryFee") }}
          </div>
          <div class="card-open-detail-info__value">
            {{
              status === "rejected"
                ? t("card.openCardHistoryRefund")
                : `${feeAmount} ${feeSymbol}`
            }}
          </div>
        </div>
        <div class="card-open-detail-info__row">
          <div class="card-open-detail-info__label">
            {{ t("card.openCardHistoryApplicationStatus") }}
          </div>
          <div class="card-open-detail-info__value" :class="`is-${status}`">
            {{ applicationStatusText }}
          </div>
        </div>
        <div class="card-open-detail-info__row">
          <div class="card-open-detail-info__label">{{ completedAtLabel }}</div>
          <div class="card-open-detail-info__value">{{ finishedAt }}</div>
        </div>
      </div>
    </section>

    <section v-if="status === 'rejected'" class="card-open-detail-kyc-tip">
      <app-image
        class="card-open-history-tips-icon"
        :src="tipsIcon"
        width="24"
        height="24"
        fit="contain"
        alt=""
        aria-hidden="true"
      />
      <div>
        <strong>{{ t("card.openCardHistorySuggestion") }}</strong>
        <span>{{ t("card.openCardHistorySuggestionDesc") }}</span>
      </div>
      <button type="button" @click="router.push('/card/kyc')">
        {{ t("card.openCardHistoryGoKyc") }}
      </button>
    </section>
  </main>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { getRouteState } from "@/utils/route-state";
import { formatCardTimestamp } from "@/view/card/utils";
import PhysicalCardOpenHistoryDetailPage from "./PhysicalCardOpenHistoryDetailPage.vue";
import pendingImage from "@/assets/images/cardImage/pending-img.png";
import physicalPendingImage from "@/assets/images/cardImage/card-open-history-physical-pending.png";
import successImage from "@/assets/images/cardImage/card-open-history-success.png";
import rejectedImage from "@/assets/images/cardImage/card-open-history-failed.png";
import supportIcon from "@/assets/images/SVG/icon/card-open-history-support.svg";
import tipsIcon from "@/assets/images/SVG/icon/card-open-history-tips.svg";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const pageState = computed(() =>
  getRouteState(route, "card-open-history-detail", {}),
);
const record = computed(() => pageState.value?.record || {});

function isPhysicalRecord(item = {}) {
  const physicalValue = item?.physical;
  if (
    physicalValue === true ||
    physicalValue === 1 ||
    String(physicalValue).trim().toLowerCase() === "true" ||
    String(physicalValue).trim() === "1"
  ) {
    return true;
  }

  const cardType = String(item?.cardType ?? item?.card_type ?? item?.type ?? "")
    .trim()
    .toUpperCase();
  if (["PHYSICAL", "1"].includes(cardType)) return true;
  if (["VIRTUAL", "2"].includes(cardType)) return false;
  return false;
}

function getStatus(item) {
  const statusValue = String(
    item?.auditStatus ??
      item?.reviewStatus ??
      item?.openStatus ??
      item?.status ??
      "",
  )
    .trim()
    .toLowerCase();

  if (
    ["pending", "review", "processing", "control", "0", "3"].includes(
      statusValue,
    ) ||
    statusValue.includes("审核中")
  ) {
    return "pending";
  }
  if (
    ["failed", "fail", "rejected", "reject", "denied", "2"].includes(
      statusValue,
    ) ||
    statusValue.includes("审核失败")
  ) {
    return "rejected";
  }
  return "rejected";
}

function getTime(value) {
  if (!value) return "--";
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? formatCardTimestamp(numericValue)
    : String(value);
}

const status = computed(
  () => pageState.value?.status || getStatus(record.value),
);
const isPhysicalCard = computed(() => isPhysicalRecord(record.value));
const cardName = computed(() =>
  String(
    record.value?.name ||
      record.value?.cardName ||
      record.value?.templateName ||
      record.value?.network ||
      t("card.prepaidCard"),
  ).trim(),
);
const feeAmount = computed(() =>
  String(
    record.value?.openAmount ??
      record.value?.amount ??
      record.value?.openChargeAmount ??
      "0",
  ),
);
const feeSymbol = computed(() =>
  String(record.value?.openSymbol || record.value?.symbol || "USDT").trim(),
);
const appliedAt = computed(() =>
  getTime(
    record.value?.createTime ||
      record.value?.createAt ||
      record.value?.applyTime ||
      record.value?.createdAt,
  ),
);
const finishedAt = computed(() =>
  getTime(
    record.value?.auditTime ||
      record.value?.reviewTime ||
      record.value?.finishTime ||
      record.value?.updateTime ||
      record.value?.updatedAt,
  ),
);
const failureReason = computed(() =>
  String(
    record.value?.reason ||
      record.value?.rejectReason ||
      record.value?.failReason ||
      t("card.openCardHistoryDefaultFailureReason"),
  ).trim(),
);
const applicationStatusText = computed(() =>
  status.value === "rejected"
    ? t("card.openCardHistoryRejectedStatus")
    : t("card.openCardHistorySuccessTitle"),
);
const completedAtLabel = computed(() =>
  status.value === "rejected"
    ? t("card.openCardHistoryFinishedAt")
    : t("card.openCardHistoryApprovedAt"),
);
</script>

<style scoped lang="scss">
.card-open-history-detail-page {
  min-height: var(--app-viewport-height);
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
  background: #ffffff;
}

.card-open-detail-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 22px 16px 0;
  text-align: center;

  h1 {
    margin: 14px 0 10px;
    color: #090a1d;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
  }
}

.card-open-detail-success-image {
  width: 138px;
  height: 123px;
  object-fit: cover;
}

.card-open-detail-amount {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  margin: 0 0 10px;
  color: #090a1d;

  strong {
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
  }

  span {
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
  }
}

.card-open-detail-status-card {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;

  &.is-approved {
    color: #07b077;
    background: #e4f8f0;
  }

  &.is-pending {
    color: #2667ff;
    background: #eaf2ff;
  }

  &.is-rejected {
    color: #ef0027;
    background: #ffe8ec;
  }
}

.card-open-detail-pending-image {
  width: 100px;
  height: 100px;
  margin-top: 1px;
  object-fit: contain;
}

.card-open-detail-pending-desc {
  max-width: 300px;
  margin: 12px 0 0;
  color: #999;
  font-size: 14px;
  line-height: 24px;
  white-space: nowrap;
}

.card-open-detail-pending-notice,
.card-open-detail-kyc-tip {
  display: flex;
  align-items: center;
  margin: 24px 16px 0;
  padding: 14px 16px;
  border-radius: 16px;
  background: #eaf2ff;
}

.card-open-detail-pending-notice {
  min-height: 70px;

  .app-image {
    width: 42px;
    height: 42px;
    flex: 0 0 42px;
    margin-right: 12px;
    object-fit: contain;
  }
}

.card-open-detail-pending-notice div,
.card-open-detail-kyc-tip div {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  text-align: left;

  strong {
    color: #090a1d;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
  }

  span {
    margin-top: 2px;
    color: #747b88;
    font-size: 12px;
    line-height: 18px;
  }
}

.card-open-detail-support {
  display: flex;
  width: calc(100% - 32px);
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 274px 16px 0;
  border: 0;
  border-radius: 999px;
  background: #d5e5ff;
  color: #2667ff;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;

  .app-image {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
  }
}

.card-open-detail-rejected-image {
  position: relative;
  display: flex;
  width: 138px;
  height: 123px;
  align-items: center;
  justify-content: center;

  &::before,
  &::after {
    position: absolute;
    border-radius: 50%;
    background: #ffe8ec;
    content: "";
  }

  &::before {
    width: 118px;
    height: 28px;
    bottom: 14px;
  }

  &::after {
    width: 10px;
    height: 10px;
    top: 18px;
    right: 22px;
  }

  .app-image {
    position: relative;
    z-index: 1;
    width: 76px;
    height: 76px;
    object-fit: contain;
  }
}

.card-open-detail-hero--rejected {
  h1 {
    margin-top: 2px;
  }
}

.card-open-detail-failure-reason {
  margin: 14px 0 0;
  color: #ef0027;
  font-size: 16px;
  line-height: 22px;
}

.card-open-detail-failure-desc {
  max-width: 332px;
  margin: 8px 0 0;
  color: #747b88;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
}

.card-open-detail-info {
  margin: 42px 16px 0;

  h2 {
    position: relative;
    margin: 0 0 20px;
    padding-left: 10px;
    color: #090a1d;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;

    &::before {
      position: absolute;
      top: 4px;
      left: 0;
      width: 4px;
      height: 14px;
      border-radius: 2px;
      background: #2667ff;
      content: "";
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 0;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    color: #333;
    font-size: 16px;
    line-height: 22px;
  }

  &__label {
    flex: 0 0 auto;
  }

  &__value {
    min-width: 0;
    color: #000;
    font-weight: 500;
    text-align: right;
    word-break: break-word;
  }

  .is-approved {
    color: #07b077;
  }

  .is-rejected {
    color: #ef0027;
  }
}

.card-open-detail-kyc-tip {
  min-height: 88px;
  padding: 14px 12px;

  .card-open-history-tips-icon {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    align-self: flex-start;
    margin: 2px 8px 0 0;
  }

  div {
    strong {
      font-size: 14px;
      line-height: 20px;
    }

    span {
      margin-top: 4px;
      line-height: 17px;
    }
  }

  button {
    width: 60px;
    height: 28px;
    flex: 0 0 auto;
    margin-left: 8px;
    border: 1px solid #2667ff;
    border-radius: 14px;
    background: #ffffff;
    color: #2667ff;
    font-size: 12px;
    line-height: 26px;
  }
}

.card-open-detail-hero--rejected + .card-open-detail-info {
  margin-top: 36px;
}
</style>
